import {AttributionManager} from './attribution';
import {CrossDomainDecorator} from './cross-domain';
import {BiqliError} from './errors';
import {BrowserLifecycle} from './lifecycle';
import {RetryQueue} from './queue';
import {AttributionStorage} from './storage';
import {TrackingTransport} from './transport';
import type {
  BiqliAnalyticsConfig,
  ClickResponse,
  EventResponse,
  QueuedEvent,
  SaleResponse,
  StripeSessionData,
  StripeSessionResponse,
  TrackClickInput,
  TrackingEventKind,
  TrackLeadInput,
  TrackSaleInput,
} from './types';
import {
  SDK_NAME,
  SDK_VERSION,
  compactObject,
  createId,
  isBrowser,
  isValidClickId,
  keyFingerprint,
  normalizeApiHost,
  toUtcTimestamp,
} from './utils';

export * from './errors';
export type * from './types';
export {SDK_NAME, SDK_VERSION} from './utils';

type TrackingResult<T> = Promise<T | QueuedEvent>;

let injectedInstance: BiqliAnalytics | null = null;

export class BiqliAnalytics {
  private readonly transport: TrackingTransport;
  private readonly attribution: AttributionManager;
  private readonly retryQueue: RetryQueue;
  private readonly crossDomain: CrossDomainDecorator;
  private readonly lifecycle: BrowserLifecycle;
  private consentGranted: boolean;
  private flushPromise: Promise<void> | null = null;
  private readonly stateListeners = new Set<() => void>();
  private stateRevision = 0;

  constructor(readonly config: BiqliAnalyticsConfig) {
    this.assertPublishableKey(config.publishableKey);
    const apiHost = normalizeApiHost(config.apiHost);
    const clientName = config.clientName?.trim() || SDK_NAME;
    const clientVersion = config.clientVersion?.trim() || SDK_VERSION;

    this.consentGranted = config.consent !== false;
    this.attribution = new AttributionManager(
      new AttributionStorage(config.cookieOptions),
      config.attributionModel ?? 'last-click',
      config.queryParameters,
    );
    if (config.clickId !== undefined && !isValidClickId(config.clickId)) {
      throw new BiqliError({
        message: 'The configured click ID is invalid.',
        code: 'invalid_click_id',
      });
    }
    this.retryQueue = new RetryQueue({
      ...config.queue,
      storageKey: config.queue?.storageKey ??
        `biqli_event_queue_v2_${keyFingerprint(config.publishableKey)}_${keyFingerprint(apiHost)}`,
    });
    this.transport = new TrackingTransport(
      apiHost,
      config.publishableKey,
      Math.max(1_000, Math.min(60_000, config.requestTimeoutMs ?? 10_000)),
      clientName,
      clientVersion,
      config.fetch,
    );
    this.crossDomain = new CrossDomainDecorator(
      config.outboundDomains,
      this.attribution.primaryQueryParameter(),
      () => this.consentGranted ? this.attribution.getClickId() : null,
    );
    this.lifecycle = new BrowserLifecycle(
      this.crossDomain,
      this.retryQueue.flushIntervalMs,
      () => {
        if (this.consentGranted) {
          this.attribution.captureFromLocation();
          this.notifyStateChange();
        }
      },
      () => void this.flush(),
    );

    if (!config.manualStart) this.start();
  }

  start(): this {
    if (!isBrowser() || this.lifecycle.isRunning()) return this;
    if (this.consentGranted) this.captureConfiguredAttribution();
    this.lifecycle.start();
    this.notifyStateChange();
    void this.flush();
    return this;
  }

  stop(): this {
    this.lifecycle.stop();
    return this;
  }

  teardown(): void {
    this.stop();
  }

  setConsent(granted: boolean): void {
    this.consentGranted = granted;
    if (!granted) {
      this.attribution.clear();
      this.retryQueue.clear();
      this.notifyStateChange();
      return;
    }
    this.captureConfiguredAttribution();
    this.notifyStateChange();
    void this.flush();
  }

  hasConsent(): boolean {
    return this.consentGranted;
  }

  getClickId(): string | null {
    return this.consentGranted ? this.attribution.getClickId() : null;
  }

  setClickId(clickId: string): void {
    this.assertConsent();
    if (!isValidClickId(clickId)) {
      throw new BiqliError({
        message: 'The click ID is invalid.',
        code: 'invalid_click_id',
      });
    }
    this.attribution.capture(clickId);
    this.notifyStateChange();
  }

  clearClickId(): void {
    this.attribution.clear();
    this.notifyStateChange();
  }

  subscribe(listener: () => void): () => void {
    this.stateListeners.add(listener);
    return () => this.stateListeners.delete(listener);
  }

  getStateRevision(): number {
    return this.stateRevision;
  }

  decorateUrl(url: string | URL): string {
    return this.crossDomain.decorate(url);
  }

  async trackClick(input: TrackClickInput): TrackingResult<ClickResponse> {
    this.assertBrowser();
    this.assertConsent();
    const eventId = input.eventId?.trim() || createId('click');
    const payload = compactObject({
      domain: input.domain,
      key: input.key,
      url: input.url ?? window.location.href,
      referrer: input.referrer === undefined ? document.referrer || undefined : input.referrer,
      eventId,
    });
    return this.deliver<ClickResponse>('click', payload, eventId);
  }

  async trackLead(input: TrackLeadInput): TrackingResult<EventResponse> {
    this.assertBrowser();
    this.assertConsent();
    const eventId = input.eventId?.trim() || createId('lead');
    const payload = compactObject({
      clickId: input.clickId ?? this.attribution.getClickId() ?? undefined,
      eventName: input.eventName,
      customerExternalId: input.customerExternalId,
      customerName: input.customerName,
      customerEmail: input.customerEmail,
      customerAvatar: input.customerAvatar,
      eventId,
      eventQuantity: input.eventQuantity,
      metadata: input.metadata,
      occurredAt: toUtcTimestamp(input.occurredAt),
    });
    return this.deliver<EventResponse>('lead', payload, eventId);
  }

  async trackSale(input: TrackSaleInput): TrackingResult<SaleResponse> {
    this.assertBrowser();
    this.assertConsent();
    const eventId = input.eventId?.trim() || createId('sale');
    const payload = compactObject({
      clickId: input.clickId ?? this.attribution.getClickId() ?? undefined,
      customerExternalId: input.customerExternalId,
      amount: input.amount,
      currency: input.currency,
      eventName: input.eventName,
      paymentProcessor: input.paymentProcessor,
      invoiceId: input.invoiceId,
      leadEventName: input.leadEventName,
      customerName: input.customerName,
      customerEmail: input.customerEmail,
      customerAvatar: input.customerAvatar,
      eventId,
      metadata: input.metadata,
      occurredAt: toUtcTimestamp(input.occurredAt),
    });
    return this.deliver<SaleResponse>('sale', payload, eventId);
  }

  async storeStripeSession(data: StripeSessionData): Promise<StripeSessionResponse> {
    this.assertBrowser();
    this.assertConsent();
    const clickId = data.bq_id?.trim() || this.attribution.getClickId();
    if (!isValidClickId(clickId)) {
      throw new BiqliError({
        message: 'No valid Biqli click ID is available for Stripe attribution.',
        code: 'missing_click_id',
      });
    }
    return this.transport.storeStripeSession({
      ...data,
      bq_id: clickId,
    });
  }

  flush(): Promise<void> {
    if (this.flushPromise) return this.flushPromise;
    if (!isBrowser() || !this.consentGranted || navigator.onLine === false) {
      return Promise.resolve();
    }

    this.flushPromise = this.flushQueue().finally(() => {
      this.flushPromise = null;
    });
    return this.flushPromise;
  }

  private async deliver<T extends ClickResponse | EventResponse | SaleResponse>(
    kind: TrackingEventKind,
    payload: Record<string, unknown>,
    eventId: string,
  ): TrackingResult<T> {
    try {
      const response = await this.transport.send<T>(kind, payload, eventId);
      this.captureClickResponse(kind, response);
      return response;
    } catch (error) {
      if (error instanceof BiqliError && error.retryable) {
        this.retryQueue.enqueue(kind, eventId, payload);
        return {queued: true, eventId};
      }
      throw error;
    }
  }

  private async flushQueue(): Promise<void> {
    for (let processed = 0; processed < 100; processed++) {
      const record = this.retryQueue.due();
      if (!record || !this.consentGranted) return;
      try {
        const response = await this.transport.send(record.kind, record.payload, record.id);
        this.captureClickResponse(record.kind, response);
        this.retryQueue.remove(record.kind, record.id);
      } catch (error) {
        if (error instanceof BiqliError && error.retryable) {
          this.retryQueue.retry(record.kind, record.id);
          return;
        }
        this.retryQueue.remove(record.kind, record.id);
      }
    }
  }

  private captureClickResponse(
    kind: TrackingEventKind,
    response: ClickResponse | EventResponse | SaleResponse,
  ): void {
    if (kind !== 'click' || !('clickId' in response) || !isValidClickId(response.clickId)) return;
    this.attribution.capture(response.clickId);
    this.notifyStateChange();
  }

  private notifyStateChange(): void {
    this.stateRevision++;
    for (const listener of this.stateListeners) listener();
  }

  private captureConfiguredAttribution(): void {
    if (this.config.clickId) this.attribution.capture(this.config.clickId);
    this.attribution.captureFromLocation();
  }

  private assertBrowser(): void {
    if (!isBrowser()) {
      throw new BiqliError({
        message: '@biqli/analytics can only send events in a browser.',
        code: 'browser_required',
      });
    }
  }

  private assertConsent(): void {
    if (!this.consentGranted) {
      throw new BiqliError({
        message: 'Tracking consent has not been granted.',
        code: 'consent_required',
      });
    }
  }

  private assertPublishableKey(key: string): void {
    if (!/^biqli_pk_[A-Za-z0-9_-]+$/.test(key)) {
      throw new BiqliError({
        message: 'Provide a valid Biqli publishable key. Secret API keys must never be used in a browser.',
        code: 'invalid_publishable_key',
      });
    }
  }
}

export {BiqliAnalytics as BiqliSDK};

export function inject(config: BiqliAnalyticsConfig): BiqliAnalytics {
  injectedInstance?.teardown();
  injectedInstance = new BiqliAnalytics(config);
  return injectedInstance;
}

export function getInjectedInstance(): BiqliAnalytics | null {
  return injectedInstance;
}

export function teardownInjectedInstance(): void {
  injectedInstance?.teardown();
  injectedInstance = null;
}
